<template>
  <div class="d-flex justify-end mb-3">
    <SoButton
      title="Create draw"
      type="primary"
      prepend-icon="mdi-plus"
      data-testid="draws-create-button"
      @click="showCreateModal = true"
    />
  </div>

  <SoAlert
    v-if="query.isError"
    title="Failed to load draws"
    data-testid="draws-error"
  />

  <SoDataTable
    v-else
    :headers="headers"
    :items="items"
    :loading="query.isFetching"
    :row-clickable="true"
    data-testid="draws-table"
    @click:row="onRowClick"
  >
    <template #item.status="{ internalItem }">
      {{ internalItem.raw.statusLabel }}
    </template>
  </SoDataTable>

  <OpsCreateDrawModal
    v-model="showCreateModal"
    :min-start-date="minStartDate"
    @created="query.refetch()"
  />
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import dayjs, { Dayjs } from "dayjs"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import { LoyaltyService } from "@/services/operations/LoyaltyService"
  import { DrawStatus, IDraw } from "@/models/operations/draw/draw.types"
  import { startDateFloor } from "@/models/operations/draw/drawHelper"
  import {
    SoDataTableColumnType,
    SoDataTableHeader,
  } from "@/types/components/SoDataTable"
  import { RouteNames } from "@/types/router"
  import OpsCreateDrawModal from "@/views/operations/loyalty/components/OpsCreateDrawModal.vue"

  const STATUS_META: Record<DrawStatus, { label: string; order: number }> = {
    upcoming: { label: "Upcoming", order: 0 },
    active: { label: "Active", order: 1 },
    closed: { label: "Closed", order: 2 },
  }

  type DrawRow = {
    id: string
    name: string
    startDateTime: Dayjs
    entryDeadlineDateTime: Dayjs
    completionDateTime: Dayjs
    status: DrawStatus
    statusLabel: string
  }

  const headers: SoDataTableHeader[] = [
    { title: "ID", key: "id", sortable: true },
    { title: "Name", key: "name", sortable: true },
    {
      title: "Start",
      key: "startDateTime",
      type: SoDataTableColumnType.DATE,
      sortable: true,
    },
    {
      title: "Entry deadline",
      key: "entryDeadlineDateTime",
      type: SoDataTableColumnType.DATE,
      sortable: true,
    },
    {
      title: "Completion",
      key: "completionDateTime",
      type: SoDataTableColumnType.DATE,
      sortable: true,
    },
    {
      title: "Status",
      key: "status",
      sortable: true,
      sort: (a: DrawStatus, b: DrawStatus) =>
        STATUS_META[a].order - STATUS_META[b].order,
    },
  ]

  export default defineComponent({
    components: {
      SoDataTable,
      SoAlert,
      SoButton,
      OpsCreateDrawModal,
    },
    data() {
      return {
        query: LoyaltyService.getDraws(),
        headers,
        showCreateModal: false,
      }
    },
    computed: {
      items(): DrawRow[] {
        const draws: IDraw[] = this.query.data ?? []
        const now = dayjs()
        return draws.map((d) => {
          const status = d.getStatus(now)
          return {
            id: d.id,
            name: d.name,
            startDateTime: d.startDateTime,
            entryDeadlineDateTime: d.entryDeadlineDateTime,
            completionDateTime: d.completionDateTime,
            status,
            statusLabel: STATUS_META[status].label,
          }
        })
      },
      minStartDate(): Date | undefined {
        return startDateFloor(this.query.data ?? [])
      },
    },
    methods: {
      onRowClick(row: DrawRow) {
        this.$router.push({
          name: RouteNames.AdminDrawDetail,
          params: { drawId: row.id },
        })
      },
    },
  })
</script>
